import React, { useState } from 'react';
import { 
  Droplets, 
  Gauge, 
  Calendar,
  Play,
  Pause,
  Settings,
  TrendingUp,
  TrendingDown,
  Zap
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { useIrrigation, formatDuration } from '../contexts/IrrigationContext';

interface Zone {
  id: string;
  name: string;
  hydraulic_group: string;
  field_name: string;
  current_moisture: number;
  target_moisture_min: number;
  target_moisture_max: number;
  pressure: number;
  status: 'optimal' | 'low' | 'high' | 'offline';
  last_irrigation: string;
  next_irrigation: string;
  area_ha: number;
}

interface ChartData {
  time: string;
  moisture: number;
  target_min: number;
  target_max: number;
}

const Zones: React.FC = () => {
  const { runtime, now, startManual, stopManual } = useIrrigation();
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [filter, setFilter] = useState('all');

  const isRunning = (id: string) => !!runtime[id]?.session;

  const startIrrigation = (zone: Zone, e: React.MouseEvent) => {
    e.stopPropagation();
    startManual(zone.id);
    toast.success(`Irrigation manuelle démarrée sur « ${zone.name} ».`);
  };

  const stopIrrigation = (zone: Zone, e: React.MouseEvent) => {
    e.stopPropagation();
    stopManual(zone.id);
    toast.success(`Irrigation arrêtée sur « ${zone.name} ».`);
  };

  // Compteur d'irrigation par zone
  const zoneCounter = (id: string): { title: string; value: string; color: string } => {
    const rt = runtime[id];
    if (rt?.session) {
      if (rt.session.kind === 'scheduled' && rt.session.ends_at) {
        return {
          title: 'Fin dans',
          value: formatDuration(new Date(rt.session.ends_at).getTime() - now),
          color: 'var(--color-success)',
        };
      }
      return {
        title: 'Manuel — irrigué depuis',
        value: formatDuration(now - new Date(rt.session.started_at).getTime()),
        color: 'var(--color-accent)',
      };
    }
    if (rt?.last_irrigated_at) {
      return {
        title: 'Dernière irrigation',
        value: `il y a ${formatDuration(now - new Date(rt.last_irrigated_at).getTime())}`,
        color: 'var(--text-primary)',
      };
    }
    return { title: 'Dernière irrigation', value: 'Jamais', color: 'var(--text-secondary)' };
  };

  const zones: Zone[] = [
    {
      id: 'zone-a1',
      name: 'Zone A-1',
      hydraulic_group: 'Group A',
      field_name: 'Domaine Les Vignes - North',
      current_moisture: 68,
      target_moisture_min: 60,
      target_moisture_max: 75,
      pressure: 3.2,
      status: 'optimal',
      last_irrigation: '2024-01-15 06:00',
      next_irrigation: '2024-01-16 06:00',
      area_ha: 2.5
    },
    {
      id: 'zone-a2', 
      name: 'Zone A-2',
      hydraulic_group: 'Group A',
      field_name: 'Domaine Les Vignes - South',
      current_moisture: 45,
      target_moisture_min: 60,
      target_moisture_max: 75,
      pressure: 2.8,
      status: 'low',
      last_irrigation: '2024-01-14 18:00',
      next_irrigation: '2024-01-15 18:00',
      area_ha: 1.8
    },
    {
      id: 'zone-b1',
      name: 'Zone B-1',
      hydraulic_group: 'Group B',
      field_name: 'Coopérative Hérault - East',
      current_moisture: 82,
      target_moisture_min: 65,
      target_moisture_max: 80,
      pressure: 3.6,
      status: 'high',
      last_irrigation: '2024-01-15 12:00',
      next_irrigation: '2024-01-17 06:00',
      area_ha: 3.2
    },
    {
      id: 'zone-b2',
      name: 'Zone B-2', 
      hydraulic_group: 'Group B',
      field_name: 'Coopérative Hérault - West',
      current_moisture: 0,
      target_moisture_min: 65,
      target_moisture_max: 80,
      pressure: 0,
      status: 'offline',
      last_irrigation: '2024-01-14 06:00',
      next_irrigation: 'Pending',
      area_ha: 2.1
    },
    {
      id: 'zone-c1',
      name: 'Zone C-1',
      hydraulic_group: 'Group C',
      field_name: 'Exploitation Gard Sud',
      current_moisture: 72,
      target_moisture_min: 60,
      target_moisture_max: 75,
      pressure: 3.1,
      status: 'optimal',
      last_irrigation: '2024-01-15 08:00',
      next_irrigation: '2024-01-16 08:00',
      area_ha: 4.1
    }
  ];

  const chartData: ChartData[] = [
    { time: '00:00', moisture: 68, target_min: 60, target_max: 75 },
    { time: '04:00', moisture: 65, target_min: 60, target_max: 75 },
    { time: '08:00', moisture: 62, target_min: 60, target_max: 75 },
    { time: '12:00', moisture: 58, target_min: 60, target_max: 75 },
    { time: '16:00', moisture: 64, target_min: 60, target_max: 75 },
    { time: '20:00', moisture: 68, target_min: 60, target_max: 75 },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'optimal': return 'var(--color-success)';
      case 'low': return 'var(--color-warning)';
      case 'high': return 'var(--color-info)';
      case 'offline': return 'var(--color-danger)';
      default: return 'var(--text-secondary)';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'optimal': return 'Optimal';
      case 'low': return 'Low Moisture';
      case 'high': return 'High Moisture';
      case 'offline': return 'Offline';
      default: return 'Unknown';
    }
  };

  const filteredZones = zones.filter(zone => {
    if (filter === 'all') return true;
    return zone.status === filter;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-heading-2 text-[var(--text-primary)]">Irrigation Zones</h1>
          <p className="text-body text-[var(--text-secondary)] mt-1">
            Monitor and manage irrigation zones across all fields
          </p>
        </div>

        {/* Filter Controls */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-[var(--text-primary)]">Filter:</span>
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
              data-testid="zone-status-filter"
            >
              <option value="all">All Zones</option>
              <option value="optimal">Optimal</option>
              <option value="low">Low Moisture</option>
              <option value="high">High Moisture</option>
              <option value="offline">Offline</option>
            </select>
          </div>
        </div>
      </div>

      {/* Zone Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredZones.map((zone) => (
          <div 
            key={zone.id}
            className={`card cursor-pointer transition-all hover:shadow-lg ${
              selectedZone?.id === zone.id ? 'ring-2 ring-[var(--color-primary)]' : ''
            }`}
            onClick={() => setSelectedZone(zone)}
            data-testid={`zone-card-${zone.id}`}
          >
            {/* Zone Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-heading-4 text-[var(--text-primary)]">{zone.name}</h3>
                <p className="text-body-sm text-[var(--text-secondary)]">{zone.field_name}</p>
                <p className="text-xs text-[var(--text-tertiary)] mt-1">
                  {zone.hydraulic_group} • {zone.area_ha} ha
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <span 
                  className="px-2 py-1 text-xs font-medium rounded-full text-white"
                  style={{ backgroundColor: getStatusColor(zone.status) }}
                >
                  {getStatusLabel(zone.status)}
                </span>
              </div>
            </div>

            {/* Moisture Level */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[var(--text-primary)]">Soil Moisture</span>
                <span className="text-lg font-bold text-[var(--text-primary)]">
                  {zone.current_moisture}%
                </span>
              </div>
              
              <div className="relative h-2 bg-[var(--gray-200)] rounded-full overflow-hidden">
                {/* Target Range Background */}
                <div 
                  className="absolute h-full bg-[var(--color-success-light)] opacity-30"
                  style={{
                    left: `${(zone.target_moisture_min / 100) * 100}%`,
                    width: `${((zone.target_moisture_max - zone.target_moisture_min) / 100) * 100}%`
                  }}
                />
                {/* Current Level */}
                <div 
                  className="h-full transition-all duration-500"
                  style={{
                    width: `${zone.current_moisture}%`,
                    backgroundColor: getStatusColor(zone.status)
                  }}
                />
              </div>
              
              <div className="flex justify-between text-xs text-[var(--text-secondary)] mt-1">
                <span>Min: {zone.target_moisture_min}%</span>
                <span>Max: {zone.target_moisture_max}%</span>
              </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center p-3 bg-[var(--gray-50)] rounded-lg">
                <Gauge className="w-5 h-5 text-[var(--color-info)] mx-auto mb-1" />
                <div className="text-lg font-semibold text-[var(--text-primary)]">
                  {zone.pressure} bar
                </div>
                <div className="text-xs text-[var(--text-secondary)]">Pressure</div>
              </div>
              
              <div className="text-center p-3 bg-[var(--gray-50)] rounded-lg" data-testid={`zone-counter-${zone.id}`}>
                <Calendar className="w-5 h-5 text-[var(--color-accent)] mx-auto mb-1" />
                <div className="text-sm font-semibold" style={{ color: zoneCounter(zone.id).color }}>
                  {zoneCounter(zone.id).value}
                </div>
                <div className="text-xs text-[var(--text-secondary)]">{zoneCounter(zone.id).title}</div>
              </div>
            </div>

            {/* Control Buttons */}
            <div className="flex space-x-2">
              <button 
                onClick={(e) => startIrrigation(zone, e)}
                disabled={isRunning(zone.id)}
                className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-[var(--color-success)] text-white rounded-lg transition-opacity ${isRunning(zone.id) ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
                title="Démarrer l'irrigation"
                data-testid={`start-irrigation-${zone.id}`}
              >
                <Play className="w-4 h-4" />
                <span className="text-sm">{isRunning(zone.id) ? 'En cours' : 'Démarrer'}</span>
              </button>
              
              <button 
                onClick={(e) => stopIrrigation(zone, e)}
                disabled={!isRunning(zone.id)}
                className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-[var(--color-danger)] text-white rounded-lg transition-opacity ${!isRunning(zone.id) ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
                title="Arrêter l'irrigation"
                data-testid={`stop-irrigation-${zone.id}`}
              >
                <Pause className="w-4 h-4" />
                <span className="text-sm">Arrêter</span>
              </button>
              
              <button 
                className="px-3 py-2 border border-[var(--border-medium)] rounded-lg hover:bg-[var(--gray-50)] transition-colors"
                data-testid={`zone-settings-${zone.id}`}
              >
                <Settings className="w-4 h-4 text-[var(--text-secondary)]" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Zone Details Panel */}
      {selectedZone && (
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-heading-3 text-[var(--text-primary)]">
                {selectedZone.name} - Detailed View
              </h2>
              <p className="text-body-sm text-[var(--text-secondary)]">
                24-hour moisture monitoring and irrigation history
              </p>
            </div>
            <button
              onClick={() => setSelectedZone(null)}
              className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              data-testid="close-zone-details"
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Chart */}
            <div className="lg:col-span-2">
              <h3 className="text-heading-4 text-[var(--text-primary)] mb-4">
                Moisture Trend (Last 24h)
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                    <XAxis dataKey="time" stroke="var(--text-secondary)" />
                    <YAxis stroke="var(--text-secondary)" domain={[0, 100]} />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'var(--surface)',
                        border: '1px solid var(--border-light)',
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-lg)'
                      }}
                    />
                    {/* Target range area */}
                    <Line 
                      type="monotone" 
                      dataKey="target_min" 
                      stroke="var(--color-success)"
                      strokeDasharray="5 5"
                      strokeWidth={1}
                      dot={false}
                      name="Target Min"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="target_max" 
                      stroke="var(--color-success)"
                      strokeDasharray="5 5"
                      strokeWidth={1}
                      dot={false}
                      name="Target Max"
                    />
                    {/* Current moisture */}
                    <Line 
                      type="monotone" 
                      dataKey="moisture" 
                      stroke="var(--color-primary)"
                      strokeWidth={3}
                      name="Current Moisture (%)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Zone Stats */}
            <div className="space-y-4">
              <h3 className="text-heading-4 text-[var(--text-primary)]">Zone Statistics</h3>
              
              <div className="space-y-4">
                <div className="p-4 bg-[var(--gray-50)] rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[var(--text-secondary)]">Current Status</span>
                    <span 
                      className="px-2 py-1 text-xs font-medium rounded-full text-white"
                      style={{ backgroundColor: getStatusColor(selectedZone.status) }}
                    >
                      {getStatusLabel(selectedZone.status)}
                    </span>
                  </div>
                </div>

                <div className="p-4 bg-[var(--gray-50)] rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[var(--text-secondary)]">Moisture Deviation</span>
                    <div className="flex items-center space-x-1">
                      {selectedZone.current_moisture > selectedZone.target_moisture_max ? (
                        <TrendingUp className="w-4 h-4 text-[var(--color-info)]" />
                      ) : selectedZone.current_moisture < selectedZone.target_moisture_min ? (
                        <TrendingDown className="w-4 h-4 text-[var(--color-warning)]" />
                      ) : (
                        <span className="text-[var(--color-success)]">✓</span>
                      )}
                    </div>
                  </div>
                  <div className="text-lg font-semibold text-[var(--text-primary)]">
                    {selectedZone.current_moisture > selectedZone.target_moisture_max 
                      ? `+${selectedZone.current_moisture - selectedZone.target_moisture_max}%`
                      : selectedZone.current_moisture < selectedZone.target_moisture_min
                      ? `${selectedZone.current_moisture - selectedZone.target_moisture_min}%`
                      : 'In Range'
                    }
                  </div>
                </div>

                <div className="p-4 bg-[var(--gray-50)] rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[var(--text-secondary)]">Last Irrigation</span>
                  </div>
                  <div className="text-sm font-medium text-[var(--text-primary)]">
                    {new Date(selectedZone.last_irrigation).toLocaleString()}
                  </div>
                </div>

                <div className="p-4 bg-[var(--gray-50)] rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[var(--text-secondary)]">Next Scheduled</span>
                  </div>
                  <div className="text-sm font-medium text-[var(--text-primary)]">
                    {selectedZone.next_irrigation === 'Pending' 
                      ? 'Pending' 
                      : new Date(selectedZone.next_irrigation).toLocaleString()
                    }
                  </div>
                </div>

                {/* Manual Controls */}
                <div className="p-4 bg-[var(--gray-50)] border border-[var(--border-light)] rounded-lg">
                  <div className="flex items-center space-x-2 mb-3">
                    <Droplets className="w-4 h-4 text-[var(--color-primary)]" />
                    <span className="text-sm font-medium text-[var(--text-primary)]">
                      Contrôle manuel
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={(e) => startIrrigation(selectedZone, e)}
                      disabled={isRunning(selectedZone.id)}
                      className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-[var(--color-success)] text-white rounded-lg transition-opacity ${isRunning(selectedZone.id) ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
                      data-testid={`detail-start-irrigation-${selectedZone.id}`}
                    >
                      <Play className="w-4 h-4" />
                      <span className="text-sm">{isRunning(selectedZone.id) ? 'En cours' : 'Démarrer'}</span>
                    </button>
                    <button
                      onClick={(e) => stopIrrigation(selectedZone, e)}
                      disabled={!isRunning(selectedZone.id)}
                      className={`flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-[var(--color-danger)] text-white rounded-lg transition-opacity ${!isRunning(selectedZone.id) ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
                      data-testid={`detail-stop-irrigation-${selectedZone.id}`}
                    >
                      <Pause className="w-4 h-4" />
                      <span className="text-sm">Arrêter</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Zones;